import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Pencil, X, GripVertical } from "lucide-react";
import DailyTasks from "@/components/DailyTasks";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  ResponsiveContainer, Tooltip, CartesianGrid, Legend,
} from "recharts";

export const PALETTE = ["#0891B2", "#1E293B", "#F59E0B", "#10B981", "#8B5CF6"];

export const fmtUnit = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "currency") {
    const a = Math.abs(v);
    if (a >= 1000000) return `$${(v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 2)}M`;
    if (a >= 10000) return `$${Math.round(v / 1000)}k`;
    return `$${Math.round(v).toLocaleString()}`;
  }
  if (unit === "percent") return `${v}%`;
  if (unit === "hours") return `${Number.isInteger(v) ? v : v.toFixed(1)} h`;
  return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1);
};

const pretty = (s) => String(s ?? "—").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const axisFmt = (unit) => (v) => (unit === "currency" ? `$${Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${(v / 1000).toFixed(0)}k`}` : unit === "percent" ? `${v}%` : v);

export function WidgetBody({ widget, data, mini }) {
  if (!data) return <div className={`${mini ? "h-24" : "h-40"} animate-pulse bg-slate-100 rounded-sm`} />;
  const h = mini ? 190 : 260;
  const seriesUnit = (label) => {
    const s = (data.series || []).find((x) => x.label === label);
    if (s?.unit) return s.unit;
    const i = (data.series_labels || []).indexOf(label);
    return (i >= 0 && data.series_units?.[i]) || widget.unit;
  };
  const tooltip = (
    <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }}
      formatter={(v, name) => fmtUnit(v, seriesUnit(name))} />
  );

  if (widget.type === "stat") {
    return (
      <div className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid={`widget-stat-${widget.id || "preview"}`}>
        {fmtUnit(data.value, widget.unit)}
      </div>
    );
  }

  if (widget.type === "gauge") {
    const unit = data.unit || widget.unit;
    const pct = data.progress_pct;
    const capped = Math.max(0, Math.min(100, pct ?? 0));
    const gh = mini ? 105 : 135;
    const ok = data.on_pace;
    return (
      <div data-testid={`widget-gauge-${widget.id || "preview"}`}>
        <div className="relative mx-auto" style={{ height: gh, maxWidth: gh * 2.4 }}>
          <ResponsiveContainer width="100%" height={gh}>
            <PieChart margin={{ top: 0, bottom: 0, left: 0, right: 0 }}>
              <Pie data={[{ v: capped }, { v: 100 - capped }]} dataKey="v" cy="100%" startAngle={180} endAngle={0}
                innerRadius={gh * 0.6} outerRadius={gh * 0.9} stroke="none" isAnimationActive={!mini}>
                <Cell fill={ok == null ? "#94A3B8" : ok ? "#059669" : "#DC2626"} />
                <Cell fill="#E2E8F0" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-x-0 bottom-0 text-center pointer-events-none">
            <div className="font-display text-2xl font-bold text-slate-900" data-testid="gauge-pct">{pct == null ? "—" : `${pct}%`}</div>
          </div>
        </div>
        <div className="text-center mt-1.5 space-y-0.5">
          <div className="font-mono text-xs text-slate-600">
            {fmtUnit(data.value, unit)} of {fmtUnit(data.target, unit)} · {data.fy_label} goal
          </div>
          {ok != null && (
            <span className={`text-[10px] font-bold uppercase tracking-wider ${ok ? "text-emerald-700" : "text-red-700"}`} data-testid="gauge-pace">
              {ok ? "On pace" : "Behind"} · {data.elapsed_pct}% of year elapsed
            </span>
          )}
          {!data.linked && <p className="text-[11px] text-slate-400">No KPI linked to this goal yet — link one on the Annual Goals page.</p>}
        </div>
      </div>
    );
  }

  if (widget.type === "funnel") {
    const stages = data.stages || [];
    const max = Math.max(...stages.map((s) => s.reached), 1);
    const COLORS = ["#0F172A", "#155E75", "#0891B2", "#059669"];
    return (
      <div className="space-y-1" data-testid={`widget-funnel-${widget.id || "preview"}`}>
        {stages.map((s, i) => (
          <div key={s.stage}>
            {i > 0 && (
              <div className="flex items-center gap-2 pl-1 py-0.5">
                <span className="text-[10px] font-bold text-cyan-700" data-testid={`funnel-conv-${widget.id || "preview"}-${s.stage}`}>
                  ↓ {s.conv_from_prev == null ? "—" : `${s.conv_from_prev}%`}
                </span>
                <span className="text-[10px] text-slate-400">converted from {stages[i - 1].label}</span>
              </div>
            )}
            <div className={`flex flex-col gap-1 ${mini ? "" : "sm:flex-row sm:items-center sm:gap-3"}`}>
              <div className="flex-1 min-w-0">
                <div className="h-8 rounded-sm flex items-center px-3 text-white font-semibold text-xs whitespace-nowrap"
                  style={{ width: `${Math.max((s.reached / max) * 100, 16)}%`, backgroundColor: COLORS[i] }}
                  data-testid={`funnel-bar-${widget.id || "preview"}-${s.stage}`}>
                  {s.label} · {s.reached}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 flex-wrap text-[10.5px] font-mono">
                {s.avg_days != null && <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-sm">avg {s.avg_days}d in stage</span>}
                {s.sitting > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-sm ${(s.sitting_avg_days ?? 0) >= 14 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                    {s.sitting} open{s.sitting_avg_days != null ? ` · ${s.sitting_avg_days}d` : ""}{(s.sitting_avg_days ?? 0) >= 14 ? " ⚠ stalling" : ""}
                  </span>
                )}
                {s.lost_from_here > 0 && <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded-sm">{s.lost_from_here} lost here</span>}
              </div>
            </div>
          </div>
        ))}
        <div className="pt-2 mt-1 border-t border-slate-100 flex items-center gap-2 text-xs">
          <span className="stamp-label text-slate-500">Overall</span>
          <span className="font-mono font-bold text-slate-900" data-testid={`funnel-overall-${widget.id || "preview"}`}>
            {data.overall_conv == null ? "—" : `${data.overall_conv}%`}
          </span>
          <span className="text-slate-400">enquiry → won · {data.total_deals} deals</span>
        </div>
      </div>
    );
  }

  if (data.columns) {
    const rows = data.columns.map((c, i) => {
      const r = { label: c.label };
      (data.series || []).forEach((s) => { r[s.label] = s.values[i]; });
      return r;
    });
    const Chart = widget.type === "line" ? LineChart : BarChart;
    return (
      <ResponsiveContainer width="100%" height={h}>
        <Chart data={rows}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis dataKey="label" stroke="#64748B" fontSize={10} />
          <YAxis stroke="#64748B" fontSize={10} tickFormatter={axisFmt(widget.unit)} width={48} />
          {tooltip}
          {(data.series || []).length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {(data.series || []).map((s, i) =>
            widget.type === "line"
              ? <Line key={s.label} type="linear" dataKey={s.label} stroke={PALETTE[i % 5]} strokeWidth={2.5} dot={{ r: 2.5 }} strokeDasharray={i === 1 ? "4 2" : undefined} isAnimationActive={!mini} />
              : <Bar key={s.label} dataKey={s.label} fill={PALETTE[i % 5]} radius={[2, 2, 0, 0]} isAnimationActive={!mini} />)}
        </Chart>
      </ResponsiveContainer>
    );
  }

  const groups = (data.groups || []).map((g) => ({ ...g, label: pretty(g.label) }));

  if (widget.type === "pie") {
    const rows = groups.map((g) => ({ name: g.label, value: g.values[0] ?? 0 }));
    return (
      <ResponsiveContainer width="100%" height={h}>
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" innerRadius={mini ? 38 : 55} outerRadius={mini ? 65 : 92} paddingAngle={2} isAnimationActive={!mini}>
            {rows.map((_, i) => <Cell key={i} fill={PALETTE[i % 5]} />)}
          </Pie>
          <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 2, fontSize: 12 }} formatter={(v) => fmtUnit(v, widget.unit)} />
          <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span style={{ color: "#475569" }}>{v}</span>} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (widget.type === "table") {
    return (
      <div className={`overflow-x-auto ${mini ? "max-h-48" : ""} overflow-y-auto`}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left stamp-label text-slate-500">
              <th className="px-3 py-2">{pretty(widget.query_def?.group_by)}</th>
              {(data.series_labels || []).map((lb) => <th key={lb} className="px-3 py-2 text-right">{lb}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groups.map((g) => (
              <tr key={g.label}>
                <td className="px-3 py-2 font-medium text-slate-800">{g.label}</td>
                {g.values.map((v, j) => (
                  <td key={j} className="px-3 py-2 text-right font-mono text-xs text-slate-700">
                    {fmtUnit(v, data.series_units?.[j] || widget.unit)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // bar breakdown
  const rows = groups.map((g) => {
    const r = { label: g.label };
    (data.series_labels || []).forEach((lb, j) => { r[lb] = g.values[j]; });
    return r;
  });
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart data={rows}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis dataKey="label" stroke="#64748B" fontSize={10} angle={-20} textAnchor="end" height={55} />
        <YAxis stroke="#64748B" fontSize={10} tickFormatter={axisFmt(widget.unit)} width={48} />
        {tooltip}
        {(data.series_labels || []).length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {(data.series_labels || []).map((lb, i) => <Bar key={lb} dataKey={lb} fill={PALETTE[i % 5]} radius={[2, 2, 0, 0]} isAnimationActive={!mini} />)}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ReportWidget({ widget, onEdit, onRemove, plain, dragHandleProps }) {
  const { data, isError } = useQuery({
    queryKey: ["report-widget-data", widget.id],
    queryFn: async () => (await api.get(`/reports/widgets/${widget.id}/data`)).data,
    enabled: widget.type !== "tasks",
  });
  const span = widget.size === "sm" ? "col-span-1" : widget.size === "full" ? "col-span-2 lg:col-span-4" : "col-span-2";
  if (widget.type === "tasks") {
    return (
      <div className={plain ? "h-full" : span} data-testid={`widget-card-${widget.id}`}>
        <DailyTasks actions={<>
          {dragHandleProps && (
            <button {...dragHandleProps} className="text-slate-400 hover:text-white cursor-grab active:cursor-grabbing" data-testid={`widget-drag-${widget.id}`}>
              <GripVertical size={15} />
            </button>
          )}
          {onEdit && (
            <button className="text-slate-400 hover:text-cyan-300" onClick={() => onEdit(widget)} data-testid={`widget-edit-${widget.id}`}>
              <Pencil size={13} />
            </button>
          )}
          {onRemove && (
            <button className="text-slate-400 hover:text-rose-300" onClick={() => onRemove(widget.id)} data-testid={`widget-remove-${widget.id}`}>
              <X size={15} />
            </button>
          )}
        </>} />
      </div>
    );
  }
  return (
    <Card className={`rounded-sm border-slate-200 industrial-shadow p-4 ${plain ? "h-full" : span}`} data-testid={`widget-card-${widget.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="stamp-label text-slate-500">{widget.type === "stat" ? "Stat" : widget.type}</span>
            <span className={`text-[8.5px] font-bold tracking-wider uppercase px-1.5 py-px rounded-sm ${widget.visibility === "management" ? "bg-slate-900 text-cyan-300" : "bg-slate-100 text-slate-500"}`}
              data-testid={`widget-vis-${widget.id}`}>
              {widget.visibility === "management" ? "MGMT" : "PUBLIC"}
            </span>
          </div>
          <h3 className={`font-display font-semibold leading-tight ${widget.size === "sm" ? "text-[13px] mt-0.5" : "text-lg mt-1"}`}>{widget.title}</h3>
          {widget.subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{widget.subtitle}</p>}
        </div>
        {dragHandleProps && (
          <button {...dragHandleProps} className="text-slate-300 hover:text-slate-600 shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" data-testid={`widget-drag-${widget.id}`}>
            <GripVertical size={14} />
          </button>
        )}
        {onEdit && (
          <button className="text-slate-400 hover:text-cyan-600 shrink-0 mt-0.5" onClick={() => onEdit(widget)} data-testid={`widget-edit-${widget.id}`}>
            <Pencil size={14} />
          </button>
        )}
        {onRemove && (
          <button className="text-slate-400 hover:text-red-600 shrink-0 mt-0.5" onClick={() => onRemove(widget.id)} data-testid={`widget-remove-${widget.id}`}>
            <X size={15} />
          </button>
        )}
      </div>
      <div className="mt-2">
        {isError
          ? <div className="text-xs text-red-500 py-4">Could not load this widget's data</div>
          : <WidgetBody widget={widget} data={data} />}
      </div>
    </Card>
  );
}
